/* Theme custom file */
import { mediaQueryLarge } from '@theme/utilities';

/** Sub-pixel slack so a snapped track counts as being at its edge or target. */
const EDGE_TOLERANCE = 1;

const KEY_DIRECTIONS = { ArrowLeft: -1, ArrowRight: 1 };

/**
 * Wraps a slideshow-component to step one card per arrow click or arrow key, and hides
 * each arrow once the track reaches that edge. It scrolls the track directly rather than
 * calling Slideshow#select, which moves a page at a time and keeps counting past the end.
 * Native scroll, drag and scroll-snap stay with the slideshow.
 */
class BestsellersCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {HTMLButtonElement | null} */
  #previousButton = null;

  /** @type {HTMLButtonElement | null} */
  #nextButton = null;

  /**
   * Scroll position the last step aims for, kept until it is reached or the user scrolls
   * by hand, so rapid clicks chain from the target instead of the mid-animation position.
   * @type {number | null}
   */
  #targetLeft = null;

  /** @type {AbortController | undefined} */
  #abortController;

  #resizeObserver = new ResizeObserver(() => this.#updateArrows());

  connectedCallback() {
    this.#scroller = this.querySelector('slideshow-slides');
    this.#previousButton = this.querySelector('[data-bestsellers-previous]');
    this.#nextButton = this.querySelector('[data-bestsellers-next]');
    if (!this.#scroller || !this.#previousButton || !this.#nextButton) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const releaseTarget = () => (this.#targetLeft = null);

    this.#previousButton.addEventListener('click', () => this.#step(-1), { signal });
    this.#nextButton.addEventListener('click', () => this.#step(1), { signal });
    this.#scroller.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    for (const type of ['pointerdown', 'wheel', 'touchstart']) {
      this.#scroller.addEventListener(type, releaseTarget, { passive: true, signal });
    }
    this.addEventListener('keydown', this.#handleKeydown, { signal });

    // Observing fires once right away, which sets the initial arrow state.
    this.#resizeObserver.observe(this.#scroller);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#resizeObserver.disconnect();
    this.#targetLeft = null;
  }

  #handleScroll = () => {
    if (this.#targetLeft !== null && Math.abs(this.#scroller.scrollLeft - this.#targetLeft) <= EDGE_TOLERANCE) {
      this.#targetLeft = null;
    }
    this.#updateArrows();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const direction = KEY_DIRECTIONS[event.key];
    if (!direction || !mediaQueryLarge.matches) return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (event.target instanceof Element && event.target.closest('input, select, textarea, [contenteditable]')) return;

    event.preventDefault();
    this.#step(direction);
  };

  /**
   * Scrolls to the snap point one card away from the current target, or from the nearest
   * snap point when no step is in flight. The target is clamped to the scrollable range
   * so it can always be reached and released.
   * @param {number} direction - -1 for previous, 1 for next
   */
  #step(direction) {
    const scroller = this.#scroller;
    const [first, second] = scroller.children;
    if (!(first instanceof HTMLElement) || !(second instanceof HTMLElement)) return;

    const cardStep = second.offsetLeft - first.offsetLeft;
    const fromIndex = Math.round((this.#targetLeft ?? scroller.scrollLeft) / cardStep);
    const maxLeft = scroller.scrollWidth - scroller.clientWidth;

    this.#targetLeft = Math.min(Math.max((fromIndex + direction) * cardStep, 0), maxLeft);
    scroller.scrollTo({ left: this.#targetLeft });
  }

  #updateArrows() {
    const { scrollLeft, scrollWidth, clientWidth } = this.#scroller;
    this.#setArrowHidden(this.#previousButton, scrollLeft <= EDGE_TOLERANCE);
    this.#setArrowHidden(this.#nextButton, scrollLeft + clientWidth >= scrollWidth - EDGE_TOLERANCE);
  }

  /**
   * Hiding a focused button would drop focus to the body, so focus moves to the
   * opposite arrow when it is visible, or to the track otherwise.
   * @param {HTMLButtonElement} button
   * @param {boolean} hidden
   */
  #setArrowHidden(button, hidden) {
    if (button.hidden === hidden) return;

    if (hidden && button === document.activeElement) {
      const opposite = button === this.#nextButton ? this.#previousButton : this.#nextButton;
      (opposite.hidden ? this.#scroller : opposite).focus({ preventScroll: true });
    }

    button.hidden = hidden;
  }
}

if (!customElements.get('bestsellers-carousel')) {
  customElements.define('bestsellers-carousel', BestsellersCarousel);
}
